import moment from 'moment-timezone';

import { MNSproCloudApi } from '../..';
import { PromiseQueue } from '../../utils/promiseQueue';
import {
  GetAllParentalMailSortBy,
  GetAllParentalMailSortDirection,
  GetAllParentalMailWardsSortBy,
  GetAllParentalMailWardsSortDirection,
  PagedOfParentalMail,
  PagedOfParentalMailResponse,
  PagedOfParentalMailWardParents,
  ParentalMail,
  ParentalMailAttachment,
  ParentalMailAttachmentDownload,
  ParentalMailCreationStatus,
  ParentalMailGroupRecipient,
  ParentalMailGroupRecipientType,
  ParentalMailReturnQuestion,
  ParentalMailReturnQuestionCreateOption,
} from '../api/v2';

/**
 * A parental mail as it is sent to the create and update endpoints. The client
 * may still be working on a draft, so every field is optional and attachments
 * are handled through their own endpoints.
 */
export type ParentalMailInput = Omit<Partial<ParentalMail>, 'attachments' | 'returnQuestions'> & {
  returnQuestions?: Partial<ParentalMailReturnQuestion>[];
};

const getApi = (authToken: string) => {
  const config = MNSproCloudApi.V2.createConfiguration(authToken);
  return MNSproCloudApi.V2.ParentalCommunicationApiFactory(config);
};

export const ParentalCommunicationParentalMailsService = {
  getAllParentalMailsQueue: new PromiseQueue(),

  /**
   * Fetches all parental mails with sorting, pagination, and optional search filtering.
   * @param search - Optional search term to filter by title or recipient name
   */
  async getAllParentalMails(
    authToken: string,
    organizationId: string,
    sortBy: GetAllParentalMailSortBy,
    sortDirection: GetAllParentalMailSortDirection,
    page: number,
    pageSize = 30,
    courseId?: string,
    sentFrom?: string,
    sentTo?: string,
    search?: string,
  ): Promise<PagedOfParentalMail> {
    return ParentalCommunicationParentalMailsService.getAllParentalMailsQueue.enqueue(
      () =>
        new Promise(resolve => {
          resolve(
            ParentalCommunicationParentalMailsService.getAllParentalMailsWorker(
              authToken,
              organizationId,
              sortBy,
              sortDirection,
              page,
              pageSize,
              courseId,
              sentFrom,
              sentTo,
              search,
            ),
          );
        }),
    );
  },

  async getAllParentalMailsWorker(
    authToken: string,
    organizationId: string,
    sortBy: GetAllParentalMailSortBy,
    sortDirection: GetAllParentalMailSortDirection,
    page: number,
    pageSize = 30,
    courseId?: string,
    sentFrom?: string,
    sentTo?: string,
    search?: string,
  ): Promise<PagedOfParentalMail> {
    const api = getApi(authToken);
    const response = await api.getAllParentalMails(
      organizationId,
      undefined,
      courseId,
      sortBy,
      sortDirection,
      sentFrom,
      sentTo,
      true, // preloadStats
      search,
      pageSize,
      page,
    );

    return response.data;
  },

  async getDetails(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<ParentalMail> {
    const api = getApi(authToken);
    const response = await api.getParentalMail(organizationId, id);

    return response.data;
  },

  async getResponses(
    authToken: string,
    organizationId: string,
    id: string,
    sortBy: GetAllParentalMailWardsSortBy,
    sortDirection: GetAllParentalMailWardsSortDirection,
    page: number,
    pageSize: number,
  ): Promise<PagedOfParentalMailResponse> {
    const api = getApi(authToken);
    const response = await api.getParentalMailResponses(
      organizationId,
      id,
      undefined,
      sortBy,
      sortDirection,
      pageSize,
      page,
    );

    return response.data;
  },

  async resendParentalMail(
    authToken: string,
    organizationId: string,
    id: string,
    recipientIds: string[],
  ): Promise<ParentalMail> {
    const api = getApi(authToken);
    const response = await api.resendParentalMail(
      organizationId,
      id,
      recipientIds,
    );

    return response.data;
  },

  async getDownloadResponsesCsv(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<ParentalMailAttachmentDownload> {
    const api = getApi(authToken);
    const response = await api.downloadResponsesCsv(organizationId, id);
    return response.data;
  },

  async create(
    authToken: string,
    organizationId: string,
    letter: ParentalMailInput,
    courseId?: string,
  ): Promise<ParentalMail> {
    const api = getApi(authToken);
    const response = await api.createParentalMail(organizationId, {
      title: letter.title || '',
      body: letter.body || '',
      recipients: letter.wholeSchool
        ? []
        : (letter.recipients
            ?.map(recipient => recipient.id)
            .filter(id => id !== undefined) as string[]) || [],
      wholeSchool: letter.wholeSchool || false,
      status: letter.status || ParentalMailCreationStatus.draft,
      requiresConfirmation: letter.requiresConfirmation || false,
      returnQuestions:
        (letter.returnQuestions as ParentalMailReturnQuestionCreateOption[]) ||
        [],
      dueDate: letter.dueDate || moment().add(7, 'day').format('YYYY-MM-DD'),
      courseId,
    });

    return response.data;
  },

  async update(
    authToken: string,
    organizationId: string,
    letter: ParentalMailInput,
    courseId?: string,
  ): Promise<ParentalMail> {
    const api = getApi(authToken);

    if (!letter.id) {
      throw new Error('Letter ID is required for update');
    }

    const response = await api.updateParentalMail(organizationId, letter.id, {
      title: letter.title || '',
      body: letter.body || '',
      recipients: letter.wholeSchool
        ? []
        : (letter.recipients
            ?.map(recipient => recipient.id)
            .filter(id => id !== undefined) as string[]) || [],
      wholeSchool: letter.wholeSchool || false,
      status: letter.status || ParentalMailCreationStatus.draft,
      requiresConfirmation: letter.requiresConfirmation || false,
      returnQuestions:
        (letter.returnQuestions as ParentalMailReturnQuestionCreateOption[]) ||
        [],
      dueDate: letter.dueDate || moment().add(7, 'day').format('YYYY-MM-DD'),
      courseId,
    });

    return response.data;
  },

  async delete(
    authToken: string,
    organizationId: string,
    id: string,
  ): Promise<void> {
    const api = getApi(authToken);
    await api.deleteParentalMail(organizationId, id);
  },

  async getRecipientStudents(
    authToken: string,
    organizationId: string,
    id: string,
    filterOnlyMissingContacts: boolean,
    sortyBy: GetAllParentalMailWardsSortBy,
    sortDirection: GetAllParentalMailWardsSortDirection,
    page: number,
    pageSize = 200,
  ): Promise<PagedOfParentalMailWardParents> {
    const api = getApi(authToken);
    const response = await api.getParentalMailWards(
      organizationId,
      id,
      undefined,
      filterOnlyMissingContacts,
      sortyBy,
      sortDirection,
      pageSize,
      page,
    );

    return response.data;
  },

  async getPossibleRecipients(
    authToken: string,
    organizationId: string,
    type: ParentalMailGroupRecipientType,
    search?: string,
    courseId?: string,
    continuationToken?: string,
    maxSize?: number,
    pageSize?: number,
  ): Promise<ParentalMailGroupRecipient[]> {
    const api = getApi(authToken);
    const response = await api.getAllParentalMailGroupRecipients(
      organizationId,
      undefined,
      type,
      search,
      courseId,
      continuationToken,
      maxSize,
      pageSize,
    );

    // Because RecipientDropdown does not support continuation tokens and
    //  onSearchQueryChange we must collect all recipients for now
    let nextLink: string | null | undefined = response.data.nextLink;
    let recipients: ParentalMailGroupRecipient[] = response.data.value;
    while (nextLink) {
      const pageResponse = await api.getAllParentalMailGroupRecipients(
        organizationId,
        undefined,
        type,
        search,
        courseId,
        nextLink,
        maxSize,
        pageSize,
      );
      recipients = recipients.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return recipients;
  },

  async updateRecipientEmailsForStudent(
    authToken: string,
    organizationId: string,
    letterId: string,
    studentId: string,
    emails: string[],
  ): Promise<void> {
    const api = getApi(authToken);
    await api.updateParentalMailAdditionalMails(organizationId, letterId, [
      {
        wardId: studentId,
        mails: emails,
      },
    ]);
  },

  async addDraftAttachment(
    authToken: string,
    organizationId: string,
    letterId: string,
    file: File,
  ): Promise<ParentalMailAttachment> {
    const api = getApi(authToken);
    const response = await api.uploadParentalMailAttachment(
      organizationId,
      letterId,
      undefined,
      file,
    );

    return response.data;
  },

  async deleteDraftAttachment(
    authToken: string,
    organizationId: string,
    letterId: string,
    attachmentId: string,
  ): Promise<void> {
    const api = getApi(authToken);
    await api.deleteParentalMailAttachment(
      organizationId,
      letterId,
      attachmentId,
    );
  },

  async getDownloadAttachmentUrl(
    authToken: string,
    organizationId: string,
    letterId: string,
    attachmentId: string,
  ): Promise<ParentalMailAttachmentDownload> {
    const api = getApi(authToken);
    const response = await api.downloadAttachment(
      organizationId,
      letterId,
      attachmentId,
    );

    return response.data;
  },

  async refreshParentalMail(
    authToken: string,
    organizationId: string,
    letterId: string,
  ): Promise<void> {
    const api = getApi(authToken);
    await api.refreshParentalMail(organizationId, letterId);
  },

  async completeParentalMail(
    authToken: string,
    organizationId: string,
    letterId: string,
  ): Promise<void> {
    const api = getApi(authToken);
    await api.completeParentalMail(organizationId, letterId);
  },
};
